import { useState, useMemo, FormEvent, useEffect } from 'react';

type AvailabilityStatus = 'available' | 'few' | 'unavailable';

interface DayAvailability {
  morning: AvailabilityStatus;
  afternoon: AvailabilityStatus;
}

const statusConfig = {
  available: { symbol: '○', color: 'text-green-500', bg: 'bg-green-50', label: 'Available' },
  few: { symbol: '△', color: 'text-orange-500', bg: 'bg-orange-50', label: 'Limited' },
  unavailable: { symbol: '✗', color: 'text-gray-400', bg: 'bg-gray-100', label: 'Unavailable' },
};

interface BookingCalendarProps {
  onClose: () => void;
}

export default function BookingCalendar({ onClose }: BookingCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<'morning' | 'afternoon' | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [availability, setAvailability] = useState<Record<string, DayAvailability>>({});
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(false);

  const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const maxDate = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 31);
    return d;
  }, [today]);

  // Fetch availability data from Supabase Edge Function
  useEffect(() => {
    const fetchAvailability = async () => {
      setIsLoadingAvailability(true);
      setAvailabilityError(false);

      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;

      try {
        const response = await fetch(
          `${supabaseUrl}/functions/v1/get-availability?year=${year}&month=${month}`,
          {
            headers: {
              'apikey': supabaseAnonKey,
              'Authorization': `Bearer ${supabaseAnonKey}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch availability');
        }

        const data = await response.json();
        setAvailability(data.availability || {});
      } catch (error) {
        console.error('Error fetching availability:', error);
        setAvailabilityError(true);
      } finally {
        setIsLoadingAvailability(false);
      }
    };

    fetchAvailability();
  }, [currentMonth, supabaseUrl, supabaseAnonKey]);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days: (Date | null)[] = [];
    
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  }, [currentMonth]);

  const canGoPrev = useMemo(() => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    return prevMonth >= new Date(today.getFullYear(), today.getMonth(), 1);
  }, [currentMonth, today]);

  const canGoNext = useMemo(() => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    return nextMonth <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
  }, [currentMonth, maxDate]);

  const handlePrevMonth = () => {
    if (canGoPrev) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
      setSelectedDate(null);
      setSelectedSlot(null);
    }
  };

  const handleNextMonth = () => {
    if (canGoNext) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
      setSelectedDate(null);
      setSelectedSlot(null);
    }
  };

  const isDateSelectable = (date: Date) => {
    return date >= today && date <= maxDate;
  };

  const getAvailability = (date: Date): DayAvailability | null => {
    const dateKey = date.toISOString().split('T')[0];
    return availability[dateKey] || null;
  };

  const handleDateClick = (date: Date) => {
    if (!isDateSelectable(date)) return;
    const dateKey = date.toISOString().split('T')[0];
    setSelectedDate(dateKey);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot: 'morning' | 'afternoon') => {
    if (!selectedDate) return;
    const dayAvailability = availability[selectedDate];
    if (dayAvailability && dayAvailability[slot] !== 'unavailable') {
      setSelectedSlot(slot);
    }
  };

  const handleProceedToForm = () => {
    if (selectedDate && selectedSlot) {
      setShowForm(true);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const bookingData = {
      full_name: formData.get('fullName') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      nationality: formData.get('nationality') as string,
      preferred_room: formData.get('preferredRoom') as string,
      move_in_date: formData.get('moveInDate') as string,
      stay_length: formData.get('stayLength') as string,
      message: formData.get('message') as string,
      referral_source: formData.get('referralSource') as string,
      selected_date: selectedDate || '',
      selected_slot: selectedSlot === 'morning' ? 'Morning (10:00-12:00)' : 'Afternoon (14:00-17:00)',
    };

    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/submit-booking`, {
        method: 'POST',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        form.reset();
      } else {
        const errorData = await response.json();
        console.error('Booking submission error:', errorData);
        setSubmitError(true);
      }
    } catch (error) {
      console.error('Network error:', error);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <div className="p-8">
          {submitSuccess ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-3xl text-green-500"></i>
              </div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Noto Serif JP' }}>
                Thank you for your inquiry!
              </h4>
              <p className="text-gray-600 mb-6">
                We have received your booking request. We will get back to you within 1-3 business days.
              </p>
              <button
                onClick={onClose}
                className="bg-orange-400 text-white px-8 py-3 rounded-full hover:bg-orange-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          ) : showForm ? (
            <>
              <button
                onClick={() => setShowForm(false)}
                className="flex items-center text-gray-500 hover:text-gray-700 mb-6 cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Calendar
              </button>

              <div className="text-center mb-8">
                <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-2">
                  Booking Inquiry
                </div>
                <h4 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                  Online Meeting Booking
                </h4>
              </div>

              <div className="bg-orange-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Selected Date</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedDate && formatDate(selectedDate)}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-4 py-2 bg-orange-400 text-white rounded-full text-sm font-medium">
                      {selectedSlot === 'morning' ? 'Morning (10:00-12:00)' : 'Afternoon (14:00-17:00)'}
                    </span>
                  </div>
                </div>
                <div className="mt-3 flex items-center text-xs text-gray-600">
                  <i className="ri-time-line mr-2"></i>
                  <span>Japan Standard Time (JST / UTC+9)</span>
                </div>
              </div>

              <form 
                id="booking-inquiry-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nationality
                  </label>
                  <input
                    type="text"
                    name="nationality"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Your nationality"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Room
                  </label>
                  <select
                    name="preferredRoom"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select a room (optional)</option>
                    <option value="Room 101">Room 101 - ¥50,000/month (Single)</option>
                    <option value="Room 102">Room 102 - ¥55,000/month (Women Only)</option>
                    <option value="Room 201">Room 201 - ¥60,000/month (Bunk Bed)</option>
                    <option value="Room 202">Room 202 - ¥45,000/month (Bunk Bed)</option>
                    <option value="Room 203">Room 203 - ¥35,000/month (Closet Bed)</option>
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Planned Move-in Date
                    </label>
                    <input
                      type="date"
                      name="moveInDate"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expected Stay Length
                    </label>
                    <select
                      name="stayLength"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="">Select duration</option>
                      <option value="1 month">1 month</option>
                      <option value="2-3 months">2-3 months</option>
                      <option value="4-6 months">4-6 months</option>
                      <option value="6+ months">6+ months</option>
                      <option value="Not sure yet">Not sure yet</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all text-sm resize-none"
                    placeholder="Tell us about your plans, questions, or anything you'd like to know..."
                  ></textarea>
                  <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How did you hear about us?
                  </label>
                  <select
                    name="referralSource"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select an option</option>
                    <option value="Google Search">Google Search</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Friend/Referral">Friend/Referral</option>
                    <option value="Digital Nomad Community">Digital Nomad Community</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {submitError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    Something went wrong. Please try again later.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-400 text-white py-4 rounded-xl hover:bg-orange-500 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting...
                    </span>
                  ) : (
                    <span className="font-medium">Submit Booking Inquiry</span>
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-2">
                  Booking Calendar
                </div>
                <h4 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                  Online Meeting Booking
                </h4>
                <p className="text-gray-600 mt-2 text-sm">
                  Please select your preferred date and time
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-500 rounded-full mr-3 flex-shrink-0">
                    <i className="ri-time-line text-white text-sm"></i>
                  </div>
                  <div className="text-sm">
                    <p className="font-semibold text-gray-900 mb-1">
                      <i className="ri-global-line mr-1"></i>
                      Time Zone Notice
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      All times are displayed in <strong>Japan Standard Time (JST / UTC+9)</strong>.
                      <br />
                      If you are accessing from outside Japan, please check your local time zone difference.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-6 mb-6 text-sm">
                {Object.entries(statusConfig).map(([key, config]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className={`text-lg font-bold ${config.color}`}>{config.symbol}</span>
                    <span className="text-gray-600">{config.label}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={handlePrevMonth}
                  disabled={!canGoPrev}
                  className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
                    canGoPrev 
                      ? 'hover:bg-gray-100 cursor-pointer text-gray-700' 
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <i className="ri-arrow-left-s-line text-xl"></i>
                </button>
                <h5 className="text-xl font-semibold text-gray-900">
                  {currentMonth.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                </h5>
                <button
                  onClick={handleNextMonth}
                  disabled={!canGoNext}
                  className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
                    canGoNext 
                      ? 'hover:bg-gray-100 cursor-pointer text-gray-700' 
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map((day, index) => (
                  <div 
                    key={day} 
                    className={`text-center text-sm font-medium py-2 ${
                      index === 0 ? 'text-red-500' : index === 6 ? 'text-blue-500' : 'text-gray-500'
                    }`}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {isLoadingAvailability ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <i className="ri-loader-4-line animate-spin text-4xl text-orange-400 mb-4"></i>
                    <p className="text-gray-600">Loading availability...</p>
                  </div>
                </div>
              ) : availabilityError ? (
                <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-center">
                  <i className="ri-error-warning-line text-3xl text-red-500 mb-3"></i>
                  <p className="text-red-600 mb-4">Failed to load availability data.</p>
                  <button
                    onClick={() => setCurrentMonth(new Date(currentMonth))}
                    className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-7 gap-1 mb-6">
                    {calendarDays.map((date, index) => {
                      if (!date) {
                        return <div key={`empty-${index}`} className="h-20"></div>;
                      }

                      const dateKey = date.toISOString().split('T')[0];
                      const isSelectable = isDateSelectable(date);
                      const isSelected = selectedDate === dateKey;
                      const dayAvailability = getAvailability(date);
                      const dayOfWeek = date.getDay();

                      return (
                        <button
                          key={dateKey}
                          onClick={() => handleDateClick(date)}
                          disabled={!isSelectable}
                          className={`h-20 rounded-lg border transition-all flex flex-col items-center justify-center p-1 ${
                            isSelected 
                              ? 'border-orange-400 bg-orange-50 ring-2 ring-orange-400' 
                              : isSelectable 
                                ? 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/50 cursor-pointer' 
                                : 'border-gray-100 bg-gray-50 cursor-not-allowed'
                          }`}
                        >
                          <span className={`text-sm font-medium mb-1 ${
                            !isSelectable 
                              ? 'text-gray-300' 
                              : dayOfWeek === 0 
                                ? 'text-red-500' 
                                : dayOfWeek === 6 
                                  ? 'text-blue-500' 
                                  : 'text-gray-900'
                          }`}>
                            {date.getDate()}
                          </span>
                          {isSelectable && dayAvailability && (
                            <div className="flex gap-1 text-xs">
                              <div className="flex flex-col items-center">
                                <span className="text-gray-400 text-[10px]">AM</span>
                                <span className={`font-bold ${statusConfig[dayAvailability.morning].color}`}>
                                  {statusConfig[dayAvailability.morning].symbol}
                                </span>
                              </div>
                              <div className="flex flex-col items-center">
                                <span className="text-gray-400 text-[10px]">PM</span>
                                <span className={`font-bold ${statusConfig[dayAvailability.afternoon].color}`}>
                                  {statusConfig[dayAvailability.afternoon].symbol}
                                </span>
                              </div>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {selectedDate && (
                    <div className="border-t border-gray-200 pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <h5 className="text-lg font-semibold text-gray-900">
                          {formatDate(selectedDate)}
                        </h5>
                        <span className="text-xs text-gray-500 flex items-center">
                          <i className="ri-time-line mr-1"></i>
                          JST (UTC+9)
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        {(['morning', 'afternoon'] as const).map((slot) => {
                          const dayAvailability = availability[selectedDate];
                          const status = dayAvailability?.[slot] || 'unavailable';
                          const config = statusConfig[status];
                          const isDisabled = status === 'unavailable';
                          const isSlotSelected = selectedSlot === slot;

                          return (
                            <button
                              key={slot}
                              onClick={() => handleSlotSelect(slot)}
                              disabled={isDisabled}
                              className={`p-4 rounded-xl border-2 transition-all ${
                                isSlotSelected
                                  ? 'border-orange-400 bg-orange-50'
                                  : isDisabled
                                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                                    : 'border-gray-200 hover:border-orange-300 cursor-pointer'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-gray-900">
                                  {slot === 'morning' ? 'Morning' : 'Afternoon'}
                                </span>
                                <span className={`text-2xl font-bold ${config.color}`}>
                                  {config.symbol}
                                </span>
                              </div>
                              <p className="text-sm text-gray-500">
                                {slot === 'morning' ? '10:00-12:00' : '14:00-17:00'}
                              </p>
                              <p className={`text-xs mt-1 ${config.color}`}>
                                {config.label}
                              </p>
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={handleProceedToForm}
                        disabled={!selectedSlot}
                        className={`w-full py-4 rounded-xl transition-all whitespace-nowrap ${
                          selectedSlot
                            ? 'bg-orange-400 text-white hover:bg-orange-500 cursor-pointer'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <span className="font-medium">Book This Time</span>
                        <i className="ri-arrow-right-line ml-2"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
